import { useState } from "react";
import {
  validateName,
  restrictedTermsError
} from "../../../utils/validateName";
import $ from "jquery";

interface EntityWithId {
  id: number;
  name: string;
  [key: string]: any;
}

interface UseEditEntityNameInGridProps<T extends EntityWithId> {
  updateAPI: (entity: T, newName: string) => Promise<void>;
  onSuccess?: (entity: T, newName: string) => void;
}

export const useEditEntityNameInGrid = <T extends EntityWithId>({
  updateAPI,
  onSuccess
}: UseEditEntityNameInGridProps<T>) => {
  const [entityNameDrafts, setEntityNameDrafts] = useState<{
    [key: number]: string;
  }>({});
  const [entitiesInEditMode, setEntitiesInEditMode] = useState<number[]>([]);
  const [entitiesInLoadingState, setEntitiesInLoadingState] = useState<
    number[]
  >([]);

  const toggleEditMode = (
    e: any,
    entityId: number,
    enterEditMode: boolean,
    name: string
  ) => {
    e.stopPropagation();
    if (enterEditMode) {
      $('.tooltip').remove();
      setEntitiesInEditMode(prev => [...prev, entityId]);
      setEntityNameDrafts(prev => ({
        ...prev,
        [entityId]: name
      }));
    } else {
      setEntitiesInEditMode(prev => prev.filter(id => id !== entityId));
      setEntityNameDrafts(prev => {
        const copy = { ...prev };
        delete copy[entityId];
        return copy;
      });
    }
  };

  const hasInvalidatedValues = (
    entityId: number,
    toastrValue: boolean,
    value: string
  ) => {
    const entityName =
      value !== undefined && value !== null
        ? value
        : entityNameDrafts[entityId];
    const editInputContainer = document.querySelector(
      `[data-edit-entity-id="${entityId}"]`
    );

    if (!validateName(entityName) && editInputContainer) {
      //@ts-ignore
      editInputContainer.style.outline = "red 1px solid";
      if (toastrValue) {
        //@ts-ignore
        toastr.error(restrictedTermsError);
      }
      return true;
    }
    if (entityName.trim() === "" && editInputContainer) {
      //@ts-ignore
      editInputContainer.style.outline = "red 1px solid";
      if (toastrValue) {
        //@ts-ignore
        toastr.error("Please enter a name");
      }
      return true;
    }
    if (
      validateName(entityName) &&
      entityName.trim() !== "" &&
      editInputContainer
    ) {
      //@ts-ignore
      editInputContainer.style.outline = "";
    }
    return false;
  };

  const updateDraftName = (e: any, entityId: number, value: string) => {
    e.stopPropagation();
    setEntityNameDrafts(prev => ({
      ...prev,
      [entityId]: value
    }));
    hasInvalidatedValues(entityId, false, (e.target as HTMLInputElement).value);
  };

  const submitNameChange = async (e: any, entity: T) => {
    e.stopPropagation();

    const newName = entityNameDrafts[entity.id];

    if (hasInvalidatedValues(entity.id, true, newName)) {
      return;
    }

    setEntitiesInLoadingState(prev => [...prev, entity.id]);

    try {
      await updateAPI(entity, newName);

      setEntityNameDrafts(prev => {
        const copy = { ...prev };
        delete copy[entity.id];
        return copy;
      });
      setEntitiesInEditMode(prev => prev.filter(id => id !== entity.id));
      setEntitiesInLoadingState(prev => prev.filter(id => id !== entity.id));

      if (onSuccess) {
        onSuccess(entity, newName);
      }
    } catch (error) {
      console.error("Error updating name:", error);
      //@ts-ignore
      toastr.error("Failed to update name. Please try again");

      setEntitiesInLoadingState(prev => prev.filter(id => id !== entity.id));
    }
  };

  const handleKeyDownAction = (e: any, entity: T) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitNameChange(e, entity);
    }
  };

  return {
    entitiesInEditMode,
    entityNameDrafts,
    entitiesInLoadingState,
    toggleEditMode,
    updateDraftName,
    handleKeyDownAction,
    submitNameChange,
    hasInvalidatedValues
  };
};
